import React from 'react';
import styled, { css } from 'styled-components';

type ButtonVariant = 'primary' | 'secondary' | 'accent' | 'outline' | 'text';
type ButtonSize = 'small' | 'medium' | 'large';

type ButtonAsProps<T extends React.ElementType> = {
  as?: T;
};

type PropsToOmit<T extends React.ElementType, P> = keyof (ButtonAsProps<T> & P);

type PolymorphicComponentProps<
  T extends React.ElementType,
  Props = {}
> = React.PropsWithChildren<Props & ButtonAsProps<T>> &
  Omit<React.ComponentPropsWithoutRef<T>, PropsToOmit<T, Props>>;

type ButtonProps<T extends React.ElementType = 'button'> = PolymorphicComponentProps<
  T,
  {
    variant?: ButtonVariant;
    size?: ButtonSize;
    fullWidth?: boolean;
  }
>;

type StyledButtonProps = {
  $variant?: ButtonVariant;
  $size?: ButtonSize;
  $fullWidth?: boolean;
};

const StyledButton = styled.button<StyledButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: all 0.3s ease;
  white-space: nowrap;
  letter-spacing: 0.03em;
  text-transform: ${({ $variant }) => $variant === 'text' ? 'none' : 'uppercase'};
  box-shadow: ${({ $variant }) => $variant !== 'text' && $variant !== 'outline' ? '0 4px 6px rgba(0, 0, 0, 0.1)' : 'none'};
  
  ${({ $fullWidth }) => $fullWidth && css`
    width: 100%;
  `}
  
  ${({ theme, $variant = 'primary' }) => {
    switch ($variant) {
      case 'primary':
        return css`
          background: ${theme.colors.gradient.primary};
          color: ${theme.colors.white};
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
          }
          &:active {
            transform: translateY(0);
          }
        `;
      case 'secondary':
        return css`
          background: ${theme.colors.gradient.secondary};
          color: ${theme.colors.white};
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
          }
          &:active {
            transform: translateY(0);
          }
        `;
      case 'accent':
        return css`
          background: ${theme.colors.gradient.accent};
          color: ${theme.colors.dark};
          font-weight: ${theme.fontWeights.medium};
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
          }
          &:active {
            transform: translateY(0);
          }
        `;
      case 'outline':
        return css`
          background: transparent;
          color: ${theme.colors.primary};
          border: 2px solid ${theme.colors.primary};
          &:hover {
            background: ${theme.colors.primary};
            color: ${theme.colors.white};
            transform: translateY(-2px);
          }
          &:active {
            transform: translateY(0);
          }
        `;
      case 'text':
        return css`
          background: transparent;
          color: ${theme.colors.primary};
          position: relative;
          &:after {
            content: '';
            position: absolute;
            width: 0;
            height: 2px;
            bottom: -2px;
            left: 0;
            background-color: ${theme.colors.accent};
            transition: width 0.3s ease;
          }
          &:hover {
            color: ${theme.colors.accent};
            &:after {
              width: 100%;
            }
          }
          &:active {
            transform: translateY(0);
          }
        `;
      default:
        return '';
    }
  }}
  
  ${({ theme, $size = 'medium' }) => {
    switch ($size) {
      case 'small':
        return css`
          font-size: ${theme.fontSizes.sm};
          padding: ${theme.space.xs} ${theme.space.md};
        `;
      case 'medium':
        return css`
          font-size: ${theme.fontSizes.md};
          padding: ${theme.space.sm} ${theme.space.lg};
        `;
      case 'large':
        return css`
          font-size: ${theme.fontSizes.lg};
          padding: ${theme.space.md} ${theme.space.xl};
        `;
      default:
        return '';
    }
  }}
`;

export const Button = <T extends React.ElementType = 'button'>({ 
  children, 
  variant = 'primary', 
  size = 'medium', 
  fullWidth = false,
  as,
  ...rest 
}: ButtonProps<T>) => {
  return (
    <StyledButton
      as={as}
      $variant={variant} 
      $size={size} 
      $fullWidth={fullWidth} 
      {...rest}
    >
      {children}
    </StyledButton>
  );
};

export default Button;
